import { SimpleSearchFilter } from 'module/api/filter/types';
import { defaultFilter } from 'module/types/filter-field/filter';
import { getApiFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-filter-transformer';
import { keyBy } from 'lodash';
import { useSalesForceTriggerFieldsQuery } from 'queries/salesforce';
import { FC, useCallback, useMemo, useState } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { BlockList } from 'components/organisms/signal/detail/block-list/list';
import { convertIntegrationFieldToField } from 'components/pages/integrations/salesforce/utils/field-conversion';
import { useAutomationContext } from './automation-context';
import { Content } from './common/content/content';
import { ConditionTypes } from './common/content/types';
import { TriggerSelectionDrawer } from './trigger-selection-drawer';

export const AutomationTriggerConditionBlock: FC = () => {
  const { integrationId, automationId } = useParams<{
    integrationId: string;
    automationId: string;
  }>();

  const isEdit = useMemo(() => Boolean(automationId), [automationId]);

  const { automationDetails, setAutomationDetails } = useAutomationContext();
  const { salesforceObject: selectedSalesforceObject, triggers } =
    automationDetails || {};

  const { value } = selectedSalesforceObject || {};

  const { data: fields, isLoading, isFetching } = useSalesForceTriggerFieldsQuery(
    parseInt(integrationId || '0'),
    String(value) || '',
    {
      enabled: Boolean(value),
    },
  );

  useEffect(() => {
    // Enhanced guard: Check if triggers exists AND has the required structure
    if (
      isEdit &&
      fields &&
      fields.length > 0 &&
      automationDetails?.triggers &&
      typeof automationDetails.triggers === 'object' &&
      'match' in automationDetails.triggers &&
      Object.keys(automationDetails.triggers).length > 1
    ) {
      const convertedFields = convertIntegrationFieldToField(fields);

      if (convertedFields && convertedFields.length > 0) {
        const lookup = keyBy(convertedFields, 'fieldId');
        const transform = getApiFilterTransformer(lookup);

        const triggers = transform.from(
          automationDetails.triggers as unknown as SimpleSearchFilter,
        );

        setAutomationDetails?.({
          triggers,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, isEdit]);

  const [triggerSelectionDrawerVisible, setTriggerSelectionDrawerVisible] =
    useState(false);
  const openTriggerSelectionDrawer = useCallback(() => {
    setTriggerSelectionDrawerVisible(true);
  }, []);
  const closeTriggerSelectionDrawer = useCallback(() => {
    setTriggerSelectionDrawerVisible(false);
  }, []);

  return (
    <>
      <BlockList.Item
        disabled={!value || isLoading || isFetching}
        onClick={openTriggerSelectionDrawer}
      >
        <BlockList.Item.Label data-required="true">
          <BlockList.Item.Label.Text>
            <FormattedMessage id="settings.integrations.create.automation.trigger.label" />
          </BlockList.Item.Label.Text>
        </BlockList.Item.Label>
        <BlockList.Item.Content>
          {selectedSalesforceObject && (
            <Content
              contentFor={ConditionTypes.Triggers}
              selectedConditions={triggers}
              salesforceObject={selectedSalesforceObject}
              loading={isLoading || isFetching}
            />
          )}
        </BlockList.Item.Content>
      </BlockList.Item>
      <TriggerSelectionDrawer
        apiStatus={isLoading || isFetching ? 'pending' : 'success'}
        value={triggers || defaultFilter}
        fields={fields ?? []}
        onClose={closeTriggerSelectionDrawer}
        visible={triggerSelectionDrawerVisible}
        title={
          <FormattedMessage id="settings.integrations.create.automation.trigger.label" />
        }
        breadcrumbs={[]}
        onSubmit={(triggers) => {
          setAutomationDetails({ triggers });
          closeTriggerSelectionDrawer();
        }}
      />
    </>
  );
};
